/**
 * Map-view create wizard — kind picker → NavStepper configure→name→confirm.
 *
 * ADR-MV-09: below-sm map_camera = dismiss chrome until host Save CTA.
 * ADR-MV-16: NavStepper on configure→name→confirm only (not kind).
 * ADR-MV-17: ≥640 non-map_camera = centered Dialog + lg:items-center (ban BottomSheet).
 * Kind immutable after POST (create-only; no PATCH kind).
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
  type FormEvent,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { BREAKPOINTS } from 'pi-kiosk-shared/responsive';
import { NavStepper } from 'pi-kiosk-shared/ui';
import {
  createMapCustomView,
  searchNominatimPlaces,
  PLACE_SEARCH_EMPTY_I18N_KEY,
  PLACE_SEARCH_EMPTY_TEST_ID,
  PLACE_SEARCH_MULTI_HIT_I18N_KEY,
  PLACE_SEARCH_MULTI_HIT_TEST_ID,
  type NominatimPlaceHit,
} from '../../account/mapViewsApi.js';
import {
  type CreateMapCustomViewBody,
  type MapCustomViewDTO,
} from '../../account/mapViewsTypes.js';
import {
  clearMapViewWizardDraft,
  readMapViewWizardDraft,
  writeMapViewWizardDraft,
  type MapViewWizardDraftV1,
} from '../../account/mapViewWizardDraft.js';
import { toCustomerFacingErrorMessage } from '../../../shared/errors/toCustomerFacingErrorMessage.js';
import { customerQueryKeys } from '../../../shared/api/queryKeys.js';
import { useOptionalOverlayBackDismiss } from '../../../shared/context/OverlayBackDismissContext.js';
import { useRuntimeConfig } from '../../../shared/providers/RuntimeConfigProvider.js';
import { useCustomerSession } from '../../../shared/session/CustomerSessionProvider.js';
import { BottomSheet } from '../../../shared/ui/BottomSheet.js';
import { Button } from '../../../shared/ui/Button.js';
import { ConfirmDialog } from '../../../shared/ui/ConfirmDialog.js';
import { Dialog } from '../../../shared/ui/Dialog.js';
import { isGloballySignedInChrome } from '../../hub/customerHubPaths.js';
import { mapViewWizardSaveErrorMessage } from './mapViewWizardSaveErrorMessage.js';
import {
  formatRadiusKm,
  MAP_VIEW_GEO_MAX_DECIMALS,
  MAP_VIEW_RADIUS_KM_DEFAULT,
  normalizeMapViewBounds,
  parseMapViewBounds,
  parseMapViewName,
  parseRadiusKm,
  roundGeo6dp,
  type MapViewKind,
  type ShopMapBoundsOverlay,
} from '../shopMapViewModel.js';
import { MapViewKindOptionCard } from './MapViewKindOptionCard.js';
import {
  defaultRadiusKmDraft,
  radiusErrorMessageKey,
  ShopMapRadiusEditor,
  validateRadiusKmDraft,
} from './ShopMapRadiusEditor.js';
import {
  isMapCameraDismissUntilSave,
  resolveMapCameraSaveCapture,
} from './shopMapViewWizardCapture.js';
/** Peer ShopMapViewsMenu / DirectoryFilterSheet — Tailwind sm = 640. */
const SM_UP_MQ = `(min-width: ${String(BREAKPOINTS.sm)}px)`;
/** Confirm summary — same ≤6 dp quantization as buildCreateBody / POST body. */
function formatConfirmGeo6dp(value: number): string {
  return roundGeo6dp(value).toFixed(MAP_VIEW_GEO_MAX_DECIMALS);
}
function formatConfirmBoundsLine(bounds: ShopMapBoundsOverlay): string {
  return `${formatConfirmGeo6dp(bounds.swLat)}, ${formatConfirmGeo6dp(bounds.swLng)} → ${formatConfirmGeo6dp(bounds.neLat)}, ${formatConfirmGeo6dp(bounds.neLng)}`;
}
const KIND_ORDER: readonly MapViewKind[] = [
  'around_me',
  'around_place',
  'map_camera',
  'exact_bounds',
] as const;
const STEPPER_STEPS = [
  { id: 'configure', labelKey: 'directory.mapView.steps.configure' },
  { id: 'name', labelKey: 'directory.mapView.steps.name' },
  { id: 'confirm', labelKey: 'directory.mapView.steps.confirm' },
] as const;
export type ShopMapViewWizardKind = MapViewKind;
export type ShopMapViewWizardStep = 'kind' | 'configure' | 'name' | 'confirm';
export interface ShopMapViewWizardPrefill {
  readonly kind?: ShopMapViewWizardKind;
  readonly radiusKm?: number;
  readonly name?: string;
  readonly centerLat?: number;
  readonly centerLng?: number;
  readonly placeLabel?: string;
  readonly swLat?: number;
  readonly swLng?: number;
  readonly neLat?: number;
  readonly neLng?: number;
  /** Resume after map_camera Save CTA / draft restore. */
  readonly step?: Exclude<ShopMapViewWizardStep, 'kind'>;
}
/** Host preview while configuring — overlays only; never filters pins. */
export type ShopMapWizardFramingPreview =
  | { readonly mode: 'none' }
  | {
      readonly mode: 'radius';
      readonly centerLat: number;
      readonly centerLng: number;
      readonly radiusKm: number;
      readonly liveGps: boolean;
    }
  | {
      readonly mode: 'bounds';
      readonly swLat: number;
      readonly swLng: number;
      readonly neLat: number;
      readonly neLng: number;
    };
export interface ShopMapViewWizardUserPosition {
  readonly lat: number;
  readonly lng: number;
}
export interface ShopMapViewWizardProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly prefill?: ShopMapViewWizardPrefill | null;
  /** Live GPS for around_me create (parent-owned; map never calls getCurrentPosition). */
  readonly userPosition: ShopMapViewWizardUserPosition | null;
  /** Request GPS when around_me configure needs it. */
  readonly onRequestMyLocation?: () => void;
  /** Current Leaflet camera SW–NE (6 dp normalized by caller or here). */
  readonly getMapBounds: () => ShopMapBoundsOverlay | null;
  /** Framing preview while wizard configures (circle / rectangle). */
  readonly onFramingPreviewChange?: (
    preview: ShopMapWizardFramingPreview,
  ) => void;
  /**
   * ADR-MV-09 — below-sm map_camera: wizard chrome dismissed; host shows Save CTA.
   * `true` while user pans freely; host bumps `mapCameraSaveToken` on Save.
   */
  readonly onMapCameraCaptureActiveChange?: (active: boolean) => void;
  /**
   * G13 — while dismiss-until-Save, configure errors (e.g. null bounds) surface on host chrome.
   */
  readonly onMapCameraCaptureErrorChange?: (error: string | null) => void;
  /** Bump from host Save CTA to capture bounds + reopen at name. */
  readonly mapCameraSaveToken?: number;
  /** Optional override; default uses createMapCustomView + query invalidate. */
  readonly onCreate?: (
    body: CreateMapCustomViewBody,
  ) => Promise<MapCustomViewDTO>;
  readonly onCreated?: (view: MapCustomViewDTO) => void;
}
function useSmUp(): boolean {
  const [smUp, setSmUp] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return true;
    }
    return window.matchMedia(SM_UP_MQ).matches;
  });
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return;
    }
    const media = window.matchMedia(SM_UP_MQ);
    const onChange = (): void => {
      setSmUp(media.matches);
    };
    onChange();
    media.addEventListener('change', onChange);
    return () => {
      media.removeEventListener('change', onChange);
    };
  }, []);
  return smUp;
}
function kindToI18nSuffix(kind: MapViewKind): string {
  switch (kind) {
    case 'around_me':
      return 'AroundMe';
    case 'around_place':
      return 'AroundPlace';
    case 'map_camera':
      return 'MapCamera';
    case 'exact_bounds':
      return 'ExactBounds';
  }
}
function boundsErrorMessageKey(
  error: 'invalid' | 'swGtNe' | 'antimeridian',
): 'directory.mapView.boundsInvalid' | 'directory.mapView.antimeridianError' {
  if (error === 'antimeridian') {
    return 'directory.mapView.antimeridianError';
  }
  return 'directory.mapView.boundsInvalid';
}
type WizardDraftState = {
  kind: MapViewKind | null;
  step: ShopMapViewWizardStep;
  radiusDraft: string;
  nameDraft: string;
  placeQuery: string;
  placeHit: NominatimPlaceHit | null;
  swLat: string;
  swLng: string;
  neLat: string;
  neLng: string;
  /** Captured map_camera bounds (after Continue / Save CTA). */
  capturedBounds: ShopMapBoundsOverlay | null;
  createdAtMs: number;
  /** Prefill locked kind — Back skips kind picker. */
  kindLocked: boolean;
};
function emptyDraft(nowMs: number): WizardDraftState {
  return {
    kind: null,
    step: 'kind',
    radiusDraft: defaultRadiusKmDraft(),
    nameDraft: '',
    placeQuery: '',
    placeHit: null,
    swLat: '',
    swLng: '',
    neLat: '',
    neLng: '',
    capturedBounds: null,
    createdAtMs: nowMs,
    kindLocked: false,
  };
}
function draftFromPrefill(
  prefill: ShopMapViewWizardPrefill,
  nowMs: number,
): WizardDraftState {
  const base = emptyDraft(nowMs);
  const kind = prefill.kind ?? null;
  const radiusDraft =
    prefill.radiusKm !== undefined
      ? formatRadiusKm(prefill.radiusKm)
      : base.radiusDraft;
  const step: ShopMapViewWizardStep =
    prefill.step ?? (kind !== null ? 'configure' : 'kind');
  return {
    ...base,
    kind,
    step: kind === null ? 'kind' : step,
    radiusDraft,
    nameDraft: prefill.name ?? '',
    placeHit:
      prefill.centerLat !== undefined &&
      prefill.centerLng !== undefined &&
      prefill.placeLabel !== undefined
        ? {
            lat: prefill.centerLat,
            lng: prefill.centerLng,
            displayName: prefill.placeLabel,
            placeId: null,
          }
        : null,
    placeQuery: prefill.placeLabel ?? '',
    swLat:
      prefill.swLat !== undefined ? String(prefill.swLat) : '',
    swLng:
      prefill.swLng !== undefined ? String(prefill.swLng) : '',
    neLat:
      prefill.neLat !== undefined ? String(prefill.neLat) : '',
    neLng:
      prefill.neLng !== undefined ? String(prefill.neLng) : '',
    capturedBounds:
      prefill.swLat !== undefined &&
      prefill.swLng !== undefined &&
      prefill.neLat !== undefined &&
      prefill.neLng !== undefined
        ? normalizeMapViewBounds({
            swLat: prefill.swLat,
            swLng: prefill.swLng,
            neLat: prefill.neLat,
            neLng: prefill.neLng,
          })
        : null,
    kindLocked: kind !== null,
  };
}
function draftFromSession(
  session: MapViewWizardDraftV1,
): WizardDraftState {
  const step: ShopMapViewWizardStep =
    session.step === 'configure' ||
    session.step === 'name' ||
    session.step === 'confirm'
      ? session.step
      : 'configure';
  return {
    kind: session.kind,
    step,
    radiusDraft:
      session.radiusKm !== undefined
        ? formatRadiusKm(session.radiusKm)
        : defaultRadiusKmDraft(),
    nameDraft: session.name ?? '',
    placeQuery: session.placeLabel ?? '',
    placeHit:
      session.centerLat !== undefined && session.centerLng !== undefined
        ? {
            lat: session.centerLat,
            lng: session.centerLng,
            displayName: session.placeLabel ?? '',
            placeId: null,
          }
        : null,
    swLat: session.swLat !== undefined ? String(session.swLat) : '',
    swLng: session.swLng !== undefined ? String(session.swLng) : '',
    neLat: session.neLat !== undefined ? String(session.neLat) : '',
    neLng: session.neLng !== undefined ? String(session.neLng) : '',
    capturedBounds:
      session.swLat !== undefined &&
      session.swLng !== undefined &&
      session.neLat !== undefined &&
      session.neLng !== undefined
        ? normalizeMapViewBounds({
            swLat: session.swLat,
            swLng: session.swLng,
            neLat: session.neLat,
            neLng: session.neLng,
          })
        : null,
    createdAtMs: session.createdAtMs,
    kindLocked: true,
  };
}
function toSessionDraft(
  state: WizardDraftState,
  userPosition: ShopMapViewWizardUserPosition | null,
): MapViewWizardDraftV1 | null {
  if (state.kind === null) {
    return null;
  }
  const draft: MapViewWizardDraftV1 = {
    kind: state.kind,
    createdAtMs: state.createdAtMs,
  };
  if (state.step !== 'kind') {
    draft.step = state.step;
  }
  const name = state.nameDraft.trim();
  if (name.length > 0) {
    draft.name = name;
  }
  const radius = parseRadiusKm(state.radiusDraft);
  if (radius.ok) {
    draft.radiusKm = radius.value;
  }
  if (state.placeHit !== null) {
    draft.centerLat = state.placeHit.lat;
    draft.centerLng = state.placeHit.lng;
    draft.placeLabel = state.placeHit.displayName;
  } else if (state.kind === 'around_me' && userPosition !== null) {
    // G3: persist around_me center from live GPS (not only around_place placeHit).
    draft.centerLat = userPosition.lat;
    draft.centerLng = userPosition.lng;
  }
  const boundsSource =
    state.capturedBounds ??
    (() => {
      const parsed = parseMapViewBounds({
        swLat: state.swLat,
        swLng: state.swLng,
        neLat: state.neLat,
        neLng: state.neLng,
      });
      return parsed.ok ? parsed.bounds : null;
    })();
  if (boundsSource !== null) {
    draft.swLat = boundsSource.swLat;
    draft.swLng = boundsSource.swLng;
    draft.neLat = boundsSource.neLat;
    draft.neLng = boundsSource.neLng;
  }
  return draft;
}
function isDirty(state: WizardDraftState): boolean {
  if (state.kind !== null) {
    return true;
  }
  if (state.nameDraft.trim().length > 0) {
    return true;
  }
  if (state.placeQuery.trim().length > 0 || state.placeHit !== null) {
    return true;
  }
  if (
    state.swLat.trim().length > 0 ||
    state.swLng.trim().length > 0 ||
    state.neLat.trim().length > 0 ||
    state.neLng.trim().length > 0
  ) {
    return true;
  }
  if (state.capturedBounds !== null) {
    return true;
  }
  if (state.radiusDraft !== defaultRadiusKmDraft()) {
    return true;
  }
  return false;
}
export function ShopMapViewWizard({
  open,
  onClose,
  prefill = null,
  userPosition,
  onRequestMyLocation,
  getMapBounds,
  onFramingPreviewChange,
  onMapCameraCaptureActiveChange,
  onMapCameraCaptureErrorChange,
  mapCameraSaveToken = 0,
  onCreate,
  onCreated,
}: ShopMapViewWizardProps): JSX.Element | null {
  const { t } = useTranslation('customer');
  const smUp = useSmUp();
  const overlayBack = useOptionalOverlayBackDismiss();
  const registerOverlayDismiss = overlayBack?.registerOverlayDismiss;
  const { apiBaseUrl } = useRuntimeConfig();
  const { session } = useCustomerSession();
  const queryClient = useQueryClient();
  const titleId = 'customer-shops-directory-wizard-title';
  // Stable mount-time clock — avoid impure Date.now() during render (react-hooks/purity).
  const [seedClockMs] = useState(() => Date.now());
  const [draft, setDraft] = useState<WizardDraftState>(() =>
    emptyDraft(seedClockMs),
  );
  const [discardOpen, setDiscardOpen] = useState(false);
  const [mapCameraCaptureActive, setMapCameraCaptureActive] = useState(false);
  const [placeSearchStatus, setPlaceSearchStatus] = useState<
    'idle' | 'loading' | 'empty' | 'hits' | 'error'
  >('idle');
  const [placeHits, setPlaceHits] = useState<readonly NominatimPlaceHit[]>([]);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [configureError, setConfigureError] = useState<string | null>(null);
  const [processedSaveToken, setProcessedSaveToken] = useState(mapCameraSaveToken);
  const [sessionHydrated, setSessionHydrated] = useState(false);
  // Start false so mount-with-open hydrates synchronously (G12).
  const [prevOpen, setPrevOpen] = useState(false);
  const [prevSmUp, setPrevSmUp] = useState(smUp);
  const signedIn = isGloballySignedInChrome({ session });
  const customerKey =
    session?.customerId ?? (signedIn ? 'session' : -1);
  const clearFramingPreview = useCallback((): void => {
    onFramingPreviewChange?.({ mode: 'none' });
  }, [onFramingPreviewChange]);
  const setCaptureActive = useCallback(
    (active: boolean): void => {
      setMapCameraCaptureActive(active);
      onMapCameraCaptureActiveChange?.(active);
    },
    [onMapCameraCaptureActiveChange],
  );
  const resetLocal = useCallback((): void => {
    setDraft(emptyDraft(seedClockMs));
    setDiscardOpen(false);
    setCaptureActive(false);
    setPlaceSearchStatus('idle');
    setPlaceHits([]);
    setSaveError(null);
    setSaving(false);
    setConfigureError(null);
    clearFramingPreview();
    setSessionHydrated(false);
  }, [clearFramingPreview, seedClockMs, setCaptureActive]);
  const finishClose = useCallback((): void => {
    clearMapViewWizardDraft();
    resetLocal();
    onClose();
  }, [onClose, resetLocal]);

  // G12 — sync open hydrate during render (React adjusting-state-when-prop-changes).
  // Ban setTimeout(0): that paints empty BottomSheet before map_camera capture.
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      let nextDraft: WizardDraftState;
      if (prefill !== null && prefill !== undefined) {
        nextDraft = draftFromPrefill(prefill, seedClockMs);
      } else {
        const sessionDraft = readMapViewWizardDraft();
        nextDraft =
          sessionDraft !== null
            ? draftFromSession(sessionDraft)
            : emptyDraft(seedClockMs);
      }
      const capture = isMapCameraDismissUntilSave({
        open: true,
        kind: nextDraft.kind,
        step: nextDraft.step,
        smUp,
      });
      setDraft(nextDraft);
      setMapCameraCaptureActive(capture);
      setSessionHydrated(true);
      setDiscardOpen(false);
      setConfigureError(null);
      setSaveError(null);
      setPlaceSearchStatus('idle');
      setPlaceHits([]);
    } else {
      setMapCameraCaptureActive(false);
      setDiscardOpen(false);
      setSessionHydrated(false);
      setConfigureError(null);
    }
  }

  // G14 — viewport cross while open retoggles dismiss-until-Save (not open-flip-only).
  if (smUp !== prevSmUp) {
    setPrevSmUp(smUp);
    if (open && sessionHydrated) {
      const capture = isMapCameraDismissUntilSave({
        open: true,
        kind: draft.kind,
        step: draft.step,
        smUp,
      });
      if (capture !== mapCameraCaptureActive) {
        setMapCameraCaptureActive(capture);
      }
    }
  }

  // G12 — derive capture for chrome so map_camera below-sm never paints sheet/Dialog first.
  const dismissUntilSave = isMapCameraDismissUntilSave({
    open,
    kind: draft.kind,
    step: draft.step,
    smUp,
  });
  if (
    open &&
    sessionHydrated &&
    dismissUntilSave !== mapCameraCaptureActive
  ) {
    setMapCameraCaptureActive(dismissUntilSave);
  }

  const captureActiveForChrome = dismissUntilSave || mapCameraCaptureActive;

  const requestClose = useCallback((): void => {
    if (saving) {
      return;
    }
    if (isDirty(draft) || captureActiveForChrome) {
      setDiscardOpen(true);
      return;
    }
    finishClose();
  }, [captureActiveForChrome, draft, finishClose, saving]);

  // Sync capture flag to host (parent setState from effect is OK).
  useEffect(() => {
    onMapCameraCaptureActiveChange?.(captureActiveForChrome);
  }, [captureActiveForChrome, onMapCameraCaptureActiveChange]);

  // G13 — surface configureError on host Save chrome while dismissed.
  useEffect(() => {
    onMapCameraCaptureErrorChange?.(
      captureActiveForChrome ? configureError : null,
    );
  }, [
    captureActiveForChrome,
    configureError,
    onMapCameraCaptureErrorChange,
  ]);

  // Clear host framing preview when wizard chrome closes.
  useEffect(() => {
    if (open) {
      return;
    }
    onFramingPreviewChange?.({ mode: 'none' });
    onMapCameraCaptureErrorChange?.(null);
  }, [open, onFramingPreviewChange, onMapCameraCaptureErrorChange]);

  // Persist draft SSOT key while open and dirty past kind.
  useEffect(() => {
    if (!open || !sessionHydrated) {
      return;
    }
    const payload = toSessionDraft(draft, userPosition);
    if (payload === null) {
      return;
    }
    writeMapViewWizardDraft(payload);
  }, [draft, open, sessionHydrated, userPosition]);

  // G10: never auto-call onRequestMyLocation on open/hydrate (permission sheet over
  // Dialog/BottomSheet). around_me GPS is gated by the explicit gps-retry CTA only.

  // OverlayBackDismiss LIFO when chrome visible (Confirm owns dismiss while open).
  useEffect(() => {
    if (
      !open ||
      discardOpen ||
      captureActiveForChrome ||
      registerOverlayDismiss === undefined
    ) {
      return;
    }
    return registerOverlayDismiss(requestClose);
  }, [
    open,
    discardOpen,
    captureActiveForChrome,
    registerOverlayDismiss,
    requestClose,
  ]);
  // During dismiss-until-Save, back still offers discard.
  useEffect(() => {
    if (
      !open ||
      !captureActiveForChrome ||
      discardOpen ||
      registerOverlayDismiss === undefined
    ) {
      return;
    }
    return registerOverlayDismiss(requestClose);
  }, [
    open,
    captureActiveForChrome,
    discardOpen,
    registerOverlayDismiss,
    requestClose,
  ]);
  // Framing preview while configuring (not during map_camera capture).
  useEffect(() => {
    if (!open || captureActiveForChrome || draft.step === 'kind') {
      clearFramingPreview();
      return;
    }
    if (draft.kind === 'around_me') {
      const parsed = parseRadiusKm(draft.radiusDraft);
      let center: { lat: number; lng: number } | null = null;
      if (userPosition !== null) {
        center = userPosition;
      } else if (draft.placeHit !== null) {
        center = { lat: draft.placeHit.lat, lng: draft.placeHit.lng };
      }
      if (!parsed.ok || center === null) {
        clearFramingPreview();
        return;
      }
      onFramingPreviewChange?.({
        mode: 'radius',
        centerLat: center.lat,
        centerLng: center.lng,
        radiusKm: parsed.value,
        liveGps: userPosition !== null,
      });
      return;
    }
    if (draft.kind === 'around_place') {
      const parsed = parseRadiusKm(draft.radiusDraft);
      if (!parsed.ok || draft.placeHit === null) {
        clearFramingPreview();
        return;
      }
      onFramingPreviewChange?.({
        mode: 'radius',
        centerLat: draft.placeHit.lat,
        centerLng: draft.placeHit.lng,
        radiusKm: parsed.value,
        liveGps: false,
      });
      return;
    }
    if (draft.kind === 'exact_bounds') {
      const parsed = parseMapViewBounds({
        swLat: draft.swLat,
        swLng: draft.swLng,
        neLat: draft.neLat,
        neLng: draft.neLng,
      });
      if (!parsed.ok) {
        clearFramingPreview();
        return;
      }
      onFramingPreviewChange?.({
        mode: 'bounds',
        ...parsed.bounds,
      });
      return;
    }
    if (draft.kind === 'map_camera' && draft.capturedBounds !== null) {
      onFramingPreviewChange?.({
        mode: 'bounds',
        ...draft.capturedBounds,
      });
      return;
    }
    clearFramingPreview();
  }, [
    captureActiveForChrome,
    clearFramingPreview,
    draft.capturedBounds,
    draft.kind,
    draft.neLat,
    draft.neLng,
    draft.placeHit,
    draft.radiusDraft,
    draft.step,
    draft.swLat,
    draft.swLng,
    onFramingPreviewChange,
    open,
    userPosition,
  ]);

  // ADR-MV-09 — host Save CTA token → capture bounds, reopen at name.
  useEffect(() => {
    if (!open || !captureActiveForChrome) {
      if (processedSaveToken !== mapCameraSaveToken) {
        const timer = window.setTimeout(() => {
          setProcessedSaveToken(mapCameraSaveToken);
        }, 0);
        return (): void => {
          window.clearTimeout(timer);
        };
      }
      return;
    }
    if (mapCameraSaveToken === processedSaveToken) {
      return;
    }
    const timer = window.setTimeout(() => {
      setProcessedSaveToken(mapCameraSaveToken);
      const resolved = resolveMapCameraSaveCapture(getMapBounds());
      if (!resolved.ok) {
        // G13 — stay on dismiss-until-Save; host banner shows configureError.
        const message = t(resolved.errorKey);
        setConfigureError(message);
        onMapCameraCaptureErrorChange?.(message);
        return;
      }
      const normalized = resolved.bounds;
      setDraft((prev) => ({
        ...prev,
        capturedBounds: normalized,
        swLat: String(normalized.swLat),
        swLng: String(normalized.swLng),
        neLat: String(normalized.neLat),
        neLng: String(normalized.neLng),
        step: resolved.nextStep,
      }));
      setConfigureError(null);
      onMapCameraCaptureErrorChange?.(null);
      setMapCameraCaptureActive(false);
    }, 0);
    return (): void => {
      window.clearTimeout(timer);
    };
  }, [
    captureActiveForChrome,
    getMapBounds,
    mapCameraSaveToken,
    onMapCameraCaptureErrorChange,
    open,
    processedSaveToken,
    t,
  ]);

  const selectKind = useCallback(
    (kind: MapViewKind): void => {
      setConfigureError(null);
      setDraft((prev) => ({
        ...prev,
        kind,
        step: 'configure',
        kindLocked: prev.kindLocked,
      }));
      if (kind === 'map_camera' && !smUp) {
        setCaptureActive(true);
      } else {
        setCaptureActive(false);
      }
      // G10: do not request GPS on kind select — wait for gps-retry CTA.
    },
    [setCaptureActive, smUp],
  );
  const validateConfigure = useCallback((): boolean => {
    setConfigureError(null);
    if (draft.kind === null) {
      return false;
    }
    if (draft.kind === 'around_me') {
      const radiusErr = validateRadiusKmDraft(draft.radiusDraft);
      if (radiusErr !== null) {
        setConfigureError(t(radiusErrorMessageKey(radiusErr)));
        return false;
      }
      if (userPosition === null && draft.placeHit === null) {
        // G10: surface error only — GPS stays behind gps-retry CTA (no OS sheet).
        setConfigureError(t('directory.mapView.gpsDeniedBody'));
        return false;
      }
      return true;
    }
    if (draft.kind === 'around_place') {
      const radiusErr = validateRadiusKmDraft(draft.radiusDraft);
      if (radiusErr !== null) {
        setConfigureError(t(radiusErrorMessageKey(radiusErr)));
        return false;
      }
      if (draft.placeHit === null) {
        setConfigureError(t('directory.mapView.placeSearchPick'));
        return false;
      }
      return true;
    }
    if (draft.kind === 'exact_bounds') {
      const parsed = parseMapViewBounds({
        swLat: draft.swLat,
        swLng: draft.swLng,
        neLat: draft.neLat,
        neLng: draft.neLng,
      });
      if (!parsed.ok) {
        setConfigureError(t(boundsErrorMessageKey(parsed.error)));
        return false;
      }
      return true;
    }
    // map_camera sm-up: snapshot camera now
    if (draft.kind === 'map_camera') {
      if (draft.capturedBounds !== null) {
        return true;
      }
      const bounds = getMapBounds();
      if (bounds === null) {
        setConfigureError(t('directory.mapView.boundsInvalid'));
        return false;
      }
      const normalized = normalizeMapViewBounds(bounds);
      setDraft((prev) => ({
        ...prev,
        capturedBounds: normalized,
        swLat: String(normalized.swLat),
        swLng: String(normalized.swLng),
        neLat: String(normalized.neLat),
        neLng: String(normalized.neLng),
      }));
      return true;
    }
    return false;
  }, [
    draft.capturedBounds,
    draft.kind,
    draft.neLat,
    draft.neLng,
    draft.placeHit,
    draft.radiusDraft,
    draft.swLat,
    draft.swLng,
    getMapBounds,
    t,
    userPosition,
  ]);
  const goNext = useCallback((): void => {
    if (draft.step === 'kind') {
      return;
    }
    if (draft.step === 'configure') {
      if (draft.kind === 'map_camera' && draft.capturedBounds === null) {
        const bounds = getMapBounds();
        if (bounds === null) {
          setConfigureError(t('directory.mapView.boundsInvalid'));
          return;
        }
        const normalized = normalizeMapViewBounds(bounds);
        setConfigureError(null);
        setDraft((prev) => ({
          ...prev,
          capturedBounds: normalized,
          swLat: String(normalized.swLat),
          swLng: String(normalized.swLng),
          neLat: String(normalized.neLat),
          neLng: String(normalized.neLng),
          step: 'name',
        }));
        return;
      }
      if (!validateConfigure()) {
        return;
      }
      setDraft((prev) => ({ ...prev, step: 'name' }));
      return;
    }
    if (draft.step === 'name') {
      const name = parseMapViewName(draft.nameDraft);
      if (name === null) {
        setConfigureError(t('directory.mapView.nameHint'));
        return;
      }
      setConfigureError(null);
      setDraft((prev) => ({ ...prev, step: 'confirm', nameDraft: name }));
    }
  }, [
    draft.capturedBounds,
    draft.kind,
    draft.nameDraft,
    draft.step,
    getMapBounds,
    t,
    validateConfigure,
  ]);
  const goBack = useCallback((): void => {
    setConfigureError(null);
    setSaveError(null);
    setDraft((prev) => {
      if (prev.step === 'confirm') {
        return { ...prev, step: 'name' };
      }
      if (prev.step === 'name') {
        if (prev.kind === 'map_camera' && !smUp) {
          setCaptureActive(true);
          return { ...prev, step: 'configure', capturedBounds: null };
        }
        return { ...prev, step: 'configure' };
      }
      if (prev.step === 'configure') {
        if (prev.kindLocked) {
          return prev;
        }
        setCaptureActive(false);
        return { ...prev, step: 'kind', kind: null };
      }
      return prev;
    });
  }, [setCaptureActive, smUp]);
  const runPlaceSearch = useCallback(
    async (event: FormEvent): Promise<void> => {
      event.preventDefault();
      const q = draft.placeQuery.trim();
      if (q.length === 0) {
        setPlaceHits([]);
        setPlaceSearchStatus('empty');
        return;
      }
      setPlaceSearchStatus('loading');
      setDraft((prev) => ({ ...prev, placeHit: null }));
      const result = await searchNominatimPlaces(q);
      if (result.kind === 'error') {
        setPlaceHits([]);
        setPlaceSearchStatus('error');
        return;
      }
      if (result.kind === 'empty') {
        setPlaceHits([]);
        setPlaceSearchStatus('empty');
        return;
      }
      setPlaceHits(result.hits);
      setPlaceSearchStatus('hits');
    },
    [draft.placeQuery],
  );
  const buildCreateBody = useCallback((): CreateMapCustomViewBody | null => {
    const name = parseMapViewName(draft.nameDraft);
    if (name === null || draft.kind === null) {
      return null;
    }
    if (draft.kind === 'around_me') {
      const radius = parseRadiusKm(draft.radiusDraft);
      let center: { lat: number; lng: number } | null = null;
      if (userPosition !== null) {
        center = userPosition;
      } else if (draft.placeHit !== null) {
        center = { lat: draft.placeHit.lat, lng: draft.placeHit.lng };
      }
      if (!radius.ok || center === null) {
        return null;
      }
      return {
        kind: 'around_me',
        name,
        radiusKm: radius.value,
        centerLat: roundGeo6dp(center.lat),
        centerLng: roundGeo6dp(center.lng),
      };
    }
    if (draft.kind === 'around_place') {
      const radius = parseRadiusKm(draft.radiusDraft);
      if (!radius.ok || draft.placeHit === null) {
        return null;
      }
      return {
        kind: 'around_place',
        name,
        radiusKm: radius.value,
        centerLat: roundGeo6dp(draft.placeHit.lat),
        centerLng: roundGeo6dp(draft.placeHit.lng),
        placeLabel: draft.placeHit.displayName,
      };
    }
    const bounds =
      draft.capturedBounds ??
      (() => {
        const parsed = parseMapViewBounds({
          swLat: draft.swLat,
          swLng: draft.swLng,
          neLat: draft.neLat,
          neLng: draft.neLng,
        });
        return parsed.ok ? parsed.bounds : null;
      })();
    if (bounds === null) {
      return null;
    }
    const normalized = normalizeMapViewBounds(bounds);
    return {
      kind: draft.kind,
      name,
      swLat: normalized.swLat,
      swLng: normalized.swLng,
      neLat: normalized.neLat,
      neLng: normalized.neLng,
    };
  }, [
    draft.capturedBounds,
    draft.kind,
    draft.nameDraft,
    draft.neLat,
    draft.neLng,
    draft.placeHit,
    draft.radiusDraft,
    draft.swLat,
    draft.swLng,
    userPosition,
  ]);
  const handleSave = useCallback(async (): Promise<void> => {
    setSaveError(null);
    const body = buildCreateBody();
    if (body === null) {
      setSaveError(t('directory.mapView.boundsInvalid'));
      return;
    }
    setSaving(true);
    try {
      const created =
        onCreate !== undefined
          ? await onCreate(body)
          : await createMapCustomView({ apiBaseUrl, body });
      await queryClient.invalidateQueries({
        queryKey: customerQueryKeys.mapCustomViews(customerKey),
      });
      clearMapViewWizardDraft();
      onCreated?.(created);
      resetLocal();
      onClose();
    } catch (err) {
      setSaveError(mapViewWizardSaveErrorMessage(err, (key) => t(key)));
    } finally {
      setSaving(false);
    }
  }, [
    apiBaseUrl,
    buildCreateBody,
    customerKey,
    onClose,
    onCreate,
    onCreated,
    queryClient,
    resetLocal,
    t,
  ]);
  const stepperSteps = useMemo(
    () =>
      STEPPER_STEPS.map((step) => ({
        id: step.id,
        label: t(step.labelKey),
      })),
    [t],
  );
  const activeStepperId =
    draft.step === 'kind' ? 'configure' : draft.step;
  const chromeTitle = ((): string => {
    if (draft.step === 'kind') {
      return t('directory.mapView.wizardTitle');
    }
    if (draft.step === 'configure') {
      return t('directory.mapView.wizardConfigureTitle');
    }
    if (draft.step === 'name') {
      return t('directory.mapView.wizardNameTitle');
    }
    return t('directory.mapView.wizardConfirmTitle');
  })();
  const radiusInvalidMessage = (() => {
    if (draft.kind !== 'around_me' && draft.kind !== 'around_place') {
      return null;
    }
    const err = validateRadiusKmDraft(draft.radiusDraft);
    return err !== null ? t(radiusErrorMessageKey(err)) : null;
  })();
  const kindPicker: ReactNode = (
    <ul className="flex flex-col gap-2">
        {KIND_ORDER.map((kind) => (
          <li key={kind}>
          <MapViewKindOptionCard
            kind={kind}
            selected={draft.kind === kind}
            onSelect={selectKind}
            title={t(`directory.mapView.wizardKind${kindToI18nSuffix(kind)}`)}
            description={t(
              `directory.mapView.wizardKind${kindToI18nSuffix(kind)}Desc`,
            )}
            testId={`customer-shops-directory-wizard-kind-${kind}`}
          />
        </li>
      ))}
    </ul>
  );
  const configureBody: ReactNode = (() => {
    if (draft.kind === 'around_me') {
      return (
        <div className="flex flex-col gap-3">
          <ShopMapRadiusEditor
            value={draft.radiusDraft}
            onChange={(next) => {
              setDraft((prev) => ({ ...prev, radiusDraft: next }));
            }}
            label={t('directory.mapView.aroundMeKmLabel')}
            ariaLabel={t('directory.mapView.aroundMeKmAria')}
            invalidMessage={radiusInvalidMessage}
            testId="customer-shops-directory-wizard-radius-km"
          />
          {userPosition === null && draft.placeHit === null ? (
            <div className="rounded-md border border-[var(--color-border)] p-3 text-sm text-[var(--color-on-surface-muted)]">
              <p>{t('directory.mapView.gpsAwaiting')}</p>
              <Button
                intent="secondary"
                type="button"
                className="mt-2"
                data-testid="customer-shops-directory-wizard-gps-retry"
                onClick={() => {
                  onRequestMyLocation?.();
                }}
              >
                {t('directory.mapView.gpsDeniedRetry')}
              </Button>
            </div>
          ) : null}
        </div>
      );
    }
    if (draft.kind === 'around_place') {
      return (
        <div className="flex flex-col gap-3">
          <form className="flex flex-col gap-2" onSubmit={(e) => void runPlaceSearch(e)}>
            <label className="flex min-w-0 flex-col gap-1 text-xs text-[var(--color-on-surface-muted)]">
              <span>{t('directory.mapView.placeSearchPlaceholder')}</span>
              <input
                type="search"
                value={draft.placeQuery}
                data-testid="place-search-input"
                className="min-h-11 w-full rounded-sm border border-[var(--color-border-strong)] bg-[var(--color-surface-elevated)] px-3 py-2 text-sm text-[var(--color-on-surface)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)]"
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  setDraft((prev) => ({
                    ...prev,
                    placeQuery: event.target.value,
                    placeHit: null,
                  }));
                }}
              />
            </label>
            <Button
              intent="secondary"
              type="submit"
              data-testid="place-search-submit"
              loading={placeSearchStatus === 'loading'}
            >
              {placeSearchStatus === 'loading'
                ? t('directory.mapView.placeSearchLoading')
                : t('directory.mapView.wizardNext')}
            </Button>
          </form>
          {placeSearchStatus === 'empty' ? (
            <p
              className="text-sm text-[var(--color-on-surface-muted)]"
              data-testid={PLACE_SEARCH_EMPTY_TEST_ID}
              role="status"
            >
              {t(PLACE_SEARCH_EMPTY_I18N_KEY)}
            </p>
          ) : null}
          {placeSearchStatus === 'error' ? (
            <p
              className="text-sm text-[var(--color-danger)]"
              data-testid="place-search-error"
              role="alert"
            >
              {t('directory.mapView.placeSearchError')}
            </p>
          ) : null}
          {placeSearchStatus === 'hits' ? (
            <div className="flex flex-col gap-2">
              {placeHits.length > 1 ? (
                <p
                  className="text-sm text-[var(--color-on-surface-muted)]"
                  data-testid={PLACE_SEARCH_MULTI_HIT_TEST_ID}
                  role="status"
                >
                  {t(PLACE_SEARCH_MULTI_HIT_I18N_KEY)}
                </p>
              ) : null}
              <ul className="flex flex-col gap-1" data-testid="place-search-hits">
                {placeHits.map((hit, index) => (
                  <li key={`${hit.displayName}-${String(hit.placeId ?? index)}`}>
            <button
              type="button"
                      className={[
                        'flex min-h-11 w-full rounded-md border px-3 py-2 text-left text-sm',
                        draft.placeHit?.displayName === hit.displayName &&
                        draft.placeHit.lat === hit.lat
                          ? 'border-[var(--color-accent)] bg-[var(--color-surface-muted)]'
                          : 'border-[var(--color-border)]',
                      ].join(' ')}
                      data-testid={`place-search-hit-${String(index)}`}
              onClick={() => {
                        setDraft((prev) => ({ ...prev, placeHit: hit }));
              }}
            >
                      {hit.displayName}
            </button>
          </li>
        ))}
      </ul>
            </div>
          ) : null}
          <ShopMapRadiusEditor
            value={draft.radiusDraft}
            onChange={(next) => {
              setDraft((prev) => ({ ...prev, radiusDraft: next }));
            }}
            label={t('directory.mapView.aroundMeKmLabel')}
            ariaLabel={t('directory.mapView.aroundMeKmAria')}
            invalidMessage={radiusInvalidMessage}
            testId="customer-shops-directory-wizard-place-radius-km"
          />
        </div>
      );
    }
    if (draft.kind === 'exact_bounds') {
      const fieldClass =
        'min-h-11 w-full rounded-sm border border-[var(--color-border-strong)] bg-[var(--color-surface-elevated)] px-3 py-2 text-sm text-[var(--color-on-surface)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)]';
      return (
        <div className="grid grid-cols-2 gap-2">
          {(
            [
              ['swLat', 'boundsSwLat', draft.swLat],
              ['swLng', 'boundsSwLng', draft.swLng],
              ['neLat', 'boundsNeLat', draft.neLat],
              ['neLng', 'boundsNeLng', draft.neLng],
            ] as const
          ).map(([key, i18nKey, value]) => (
            <label
              key={key}
              className="flex min-w-0 flex-col gap-1 text-xs text-[var(--color-on-surface-muted)]"
            >
              <span>{t(`directory.mapView.${i18nKey}`)}</span>
              <input
                type="text"
                inputMode="decimal"
                value={value}
                data-testid={`customer-shops-directory-wizard-${key}`}
                className={fieldClass}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  const next = event.target.value;
                  setDraft((prev) => ({ ...prev, [key]: next }));
                }}
              />
            </label>
          ))}
        </div>
      );
    }
    if (draft.kind === 'map_camera') {
      return (
        <p className="text-sm text-[var(--color-on-surface-muted)]">
          {t('directory.mapView.wizardMapCameraSplitHint')}
        </p>
      );
    }
    return null;
  })();
  const nameBody: ReactNode = (
    <label className="flex min-w-0 flex-col gap-1 text-xs text-[var(--color-on-surface-muted)]">
      <span>{t('directory.mapView.nameLabel')}</span>
      <input
        type="text"
        maxLength={64}
        value={draft.nameDraft}
        data-testid="customer-shops-directory-wizard-name"
        className="min-h-11 w-full rounded-sm border border-[var(--color-border-strong)] bg-[var(--color-surface-elevated)] px-3 py-2 text-sm text-[var(--color-on-surface)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)]"
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          setDraft((prev) => ({ ...prev, nameDraft: event.target.value }));
        }}
      />
      <span className="text-[var(--color-on-surface-muted)]">
        {t('directory.mapView.nameHint')}
      </span>
    </label>
  );
  const confirmSummary: ReactNode = (() => {
    if (draft.kind === null) {
      return null;
    }
    const kindLabel = t(
      `directory.mapView.wizardKind${kindToI18nSuffix(draft.kind)}`,
    );
    const radius =
      draft.kind === 'around_me' || draft.kind === 'around_place'
        ? parseRadiusKm(draft.radiusDraft)
        : null;
    return (
      <div
        className="flex flex-col gap-2 text-sm text-[var(--color-on-surface)]"
        data-testid="customer-shops-directory-wizard-confirm-summary"
      >
        <p>
          <span className="text-[var(--color-on-surface-muted)]">
            {kindLabel}
          </span>
        </p>
        <p className="font-medium">{draft.nameDraft}</p>
        {radius?.ok === true ? (
          <p>
            {formatRadiusKm(radius.value)} km
            {draft.kind === 'around_place' && draft.placeHit !== null
              ? ` · ${draft.placeHit.displayName}`
              : ''}
          </p>
        ) : null}
        {(draft.kind === 'map_camera' || draft.kind === 'exact_bounds') &&
        draft.capturedBounds !== null ? (
          <p className="tabular-nums text-[var(--color-on-surface-muted)]">
            {formatConfirmBoundsLine(draft.capturedBounds)}
          </p>
        ) : null}
        {draft.kind === 'exact_bounds' && draft.capturedBounds === null
          ? (() => {
              const parsed = parseMapViewBounds({
                swLat: draft.swLat,
                swLng: draft.swLng,
                neLat: draft.neLat,
                neLng: draft.neLng,
              });
              return (
                <p className="tabular-nums text-[var(--color-on-surface-muted)]">
                  {parsed.ok
                    ? formatConfirmBoundsLine(parsed.bounds)
                    : `${draft.swLat}, ${draft.swLng} → ${draft.neLat}, ${draft.neLng}`}
                </p>
              );
            })()
          : null}
      </div>
    );
  })();
  const footer: ReactNode = (
    <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
      {draft.step !== 'kind' && !(draft.step === 'configure' && draft.kindLocked) ? (
        <Button
          intent="secondary"
          type="button"
          disabled={saving}
          data-testid="customer-shops-directory-wizard-back"
          onClick={goBack}
        >
          {t('directory.mapView.wizardBack')}
        </Button>
      ) : (
        <Button
          intent="secondary"
          type="button"
          disabled={saving}
          data-testid="customer-shops-directory-wizard-cancel"
          onClick={requestClose}
        >
          {t('directory.mapView.wizardCancel')}
        </Button>
      )}
      {draft.step === 'confirm' ? (
        <Button
          intent="primary"
          type="button"
          loading={saving}
          data-testid="customer-shops-directory-wizard-save"
          onClick={() => {
            void handleSave();
          }}
        >
          {t('directory.mapView.wizardSave')}
        </Button>
      ) : null}
      {draft.step !== 'confirm' && draft.step !== 'kind' ? (
        <Button
          intent="primary"
          type="button"
          disabled={saving}
          data-testid="customer-shops-directory-wizard-next"
          onClick={goNext}
        >
          {t('directory.mapView.wizardNext')}
        </Button>
      ) : null}
    </div>
  );
  const body: ReactNode = (
    <>
      {draft.step !== 'kind' ? (
        <NavStepper
          steps={stepperSteps}
          activeStepId={activeStepperId}
          ariaLabel={t('directory.mapView.wizardTitle')}
          testId="customer-shops-directory-wizard-stepper"
          className="mb-3"
        />
      ) : null}
      {draft.step === 'kind' ? kindPicker : null}
      {draft.step === 'configure' ? configureBody : null}
      {draft.step === 'name' ? nameBody : null}
      {draft.step === 'confirm' ? confirmSummary : null}
      {configureError !== null ? (
        <p
          className="mt-2 text-sm text-[var(--color-danger)]"
          role="alert"
          data-testid="customer-shops-directory-wizard-configure-error"
        >
          {configureError}
        </p>
      ) : null}
      {saveError !== null ? (
        <p
          className="mt-2 text-sm text-[var(--color-danger)]"
          role="alert"
          data-testid="customer-shops-directory-wizard-save-error"
        >
          {toCustomerFacingErrorMessage(saveError, {
            fallback: t('directory.mapView.placeSearchError'),
          })}
        </p>
      ) : null}
      {footer}
    </>
  );
  const discard = (
    <ConfirmDialog
      open={discardOpen}
      title={t('directory.mapView.confirmDiscardTitle')}
      description={t('directory.mapView.confirmDiscardBody')}
      confirmLabel={t('directory.mapView.confirmDiscardConfirm')}
      cancelLabel={t('directory.mapView.wizardCancel')}
      confirmIntent="danger"
      testId="customer-shops-directory-wizard-discard-confirm"
      onCancel={() => {
        setDiscardOpen(false);
      }}
      onConfirm={() => {
        setDiscardOpen(false);
        finishClose();
      }}
    />
  );
  // ADR-MV-09: chrome fully dismissed — host Save CTA reopens name step.
  if (!open) {
    return null;
  }
  // G12 — never paint empty BottomSheet before sync hydrate / map_camera capture.
  if (!sessionHydrated) {
    return discard;
  }
  if (captureActiveForChrome) {
    return discard;
  }

  // ADR-MV-17: ≥640 non-map_camera → Dialog + lg:items-center ONLY (ban BottomSheet).
  // below-sm non-map_camera: BottomSheet peer DirectoryFilterSheet.
  // map_camera configure ≥640: non-modal split panel (map stays interactive).
  // map_camera name/confirm: Dialog after capture / Continue.
  const belowSmSheet = !smUp && draft.kind !== 'map_camera';
  const mapCameraSplitPanel =
    smUp && draft.kind === 'map_camera' && draft.step === 'configure';

  if (belowSmSheet) {
    return (
      <>
        <BottomSheet
          open={open}
          onClose={requestClose}
          title={chromeTitle}
          titleId={titleId}
          closeLabel={t('directory.mapView.wizardCancel')}
          testId="customer-shops-directory-wizard"
          className="max-h-[90vh] bg-[var(--color-surface-elevated)] p-5 lg:max-w-lg"
          containerClassName="lg:items-center"
          pending={saving}
        >
          {body}
        </BottomSheet>
        {discard}
      </>
    );
  }

  if (mapCameraSplitPanel) {
    return (
      <>
        <div
          className="fixed bottom-4 left-4 z-[60] w-[min(22rem,calc(100vw-2rem))] max-h-[min(70vh,32rem)] overflow-auto rounded-xl border border-[var(--color-border)] bg-[var(--color-surface-elevated)] p-4 shadow-[var(--shadow-popover)]"
          role="dialog"
          aria-modal="false"
          aria-labelledby={titleId}
          data-testid="customer-shops-directory-wizard"
        >
          <h2
            id={titleId}
            className="mb-3 text-base font-semibold text-[var(--color-on-surface)]"
          >
            {chromeTitle}
          </h2>
          {body}
      </div>
        {discard}
      </>
    );
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={requestClose}
        title={chromeTitle}
        titleId={titleId}
        closeLabel={t('directory.mapView.wizardCancel')}
        testId="customer-shops-directory-wizard"
        className="max-w-md"
        shellClassName="lg:items-center"
        pending={saving}
      >
        {body}
    </Dialog>
      {discard}
    </>
  );
}

/** Default radius for tests / callers. */
export const SHOP_MAP_VIEW_WIZARD_DEFAULT_RADIUS_KM = MAP_VIEW_RADIUS_KM_DEFAULT;
